import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Mail, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api } from "../App";

const AuthShell = ({ children }) => (
  <div className="min-h-screen bg-background font-body flex flex-col">
    <header className="px-6 md:px-12 py-6">
      <Link to="/" className="inline-flex items-center gap-2">
        <img src="/logo.png" alt="PostWriter" className="w-8 h-8" />
        <span className="text-xl font-extrabold tracking-tighter font-headline text-foreground">
          PostWriter
        </span>
      </Link>
    </header>
    <main className="flex-1 flex items-center justify-center px-6 pb-16">
      <div className="w-full max-w-sm">{children}</div>
    </main>
  </div>
);

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Backend always returns the same generic response regardless of
      // whether the account exists — no success/failure branching here,
      // to match that anti-enumeration behavior.
      await api.post("/auth/forgot-password", { email });
    } finally {
      setIsSubmitting(false);
      setSubmitted(true);
    }
  };

  if (submitted) {
    return (
      <AuthShell>
        <Helmet>
          <title>Check your email — PostWriter</title>
        </Helmet>
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-secondary mb-6">
            <Mail className="w-5 h-5 text-foreground" />
          </div>
          <h1 className="font-headline text-2xl font-extrabold tracking-tight text-foreground mb-3">
            Check your email
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed mb-8">
            If an account exists for <span className="text-foreground font-medium">{email}</span>, we've sent a
            link to reset your password. It expires in 1 hour.
          </p>
          <Link to="/login" className="text-sm text-muted-foreground hover:text-primary transition-colors">
            Back to login
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <Helmet>
        <title>Forgot password — PostWriter</title>
      </Helmet>
      <h1 className="font-headline text-2xl font-extrabold tracking-tight text-foreground mb-1">
        Forgot your password?
      </h1>
      <p className="text-sm text-muted-foreground mb-8">
        Enter your email and we'll send you a reset link.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? "Sending…" : "Send reset link"}
          {!isSubmitting && <ArrowRight className="w-4 h-4" />}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        <Link to="/login" className="text-foreground font-medium hover:text-primary transition-colors">
          Back to login
        </Link>
      </p>
    </AuthShell>
  );
};

export default ForgotPasswordPage;
